import { get } from 'lodash/fp';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import DayJSUtc from 'dayjs/plugin/utc';
import * as advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(DayJSUtc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

import {
  User,
  Division,
  Community,
  TeamMember as Contact,
  TeamMemberType as ContactType,
  DefaultPhase,
  Phase,
  Plan,
  Document,
  Lot,
  Elevation,
  LotGalleryImage,
  PhaseTip,
  LotAppointmentStatus,
  LotAppointment,
  LotDocument,
  Conversation,
  Company,
  ConversationStatusEnum,
  Lot149PhotosGallery
} from '@tripointe/tripointe-cms/data-access';
import {
  LoginsToDataSourcePayload,
  TableAction,
  TableEntry,
  TableOption
} from '@tripointe/tripointe-cms/types';
import {
  BASE_USERS_TABLE_CONFIG,
  BASE_USERS_ARCHIVED_OPTIONS,
  BASE_USERS_ACTIVE_OPTIONS,
  DIVISIONS_ARCHIVED_OPTIONS,
  DIVISIONS_ACTIVE_OPTIONS,
  DIVISIONS_TABLE_CONFIG,
  COMMUNITIES_ACTIVE_OPTIONS,
  COMMUNITIES_TABLE_CONFIG,
  COMMUNITIES_ARCHIVED_OPTIONS,
  CONTACTS_ACTIVE_OPTIONS,
  CONTACTS_ARCHIVED_OPTIONS,
  CONTACTS_TABLE_CONFIG,
  CONTACT_TYPES_ARCHIVED_OPTIONS,
  CONTACT_TYPES_ACTIVE_OPTIONS,
  CONTACT_TYPES_TABLE_CONFIG,
  BASE_TEAM_MEMBERS_TABLE_CONFIG,
  PHASES_ARCHIVED_OPTIONS,
  PHASES_ACTIVE_OPTIONS,
  PHASES_TABLE_CONFIG,
  DEFAULT_PHASES_TABLE_CONFIG,
  PLANS_ARCHIVED_OPTIONS,
  PLANS_ACTIVE_OPTIONS,
  PLANS_TABLE_CONFIG,
  BASE_EVENTS_ARCHIVED_OPTIONS,
  BASE_EVENTS_ACTIVE_OPTIONS,
  BASE_EVENTS_TABLE_CONFIG,
  BASE_DOCUMENTS_ARCHIVED_OPTIONS,
  BASE_DOCUMENTS_ACTIVE_OPTIONS,
  BASE_DOCUMENTS_TABLE_CONFIG,
  BASE_HOMES_ARCHIVED_OPTIONS,
  BASE_HOMES_ACTIVE_OPTIONS,
  BASE_HOME_PHOTOS_149_TABLE_CONFIG,
  BASE_HOME_LOGINS_OPTIONS,
  BASE_HOME_LOGINS_TABLE_CONFIG,
  ELEVATIONS_ACTIVE_OPTIONS,
  ELEVATIONS_TABLE_CONFIG,
  PHASE_TIPS_ACTIVE_OPTIONS,
  PHASE_TIPS_ARCHIVED_OPTIONS,
  PHASE_TIPS_TABLE_CONFIG,
  PHASE_TIPS_ARCHIVED_TABLE_CONFIG,
  BASE_HOME_SCHEDULE_OPTIONS,
  BASE_HOME_SCHEDULE_TABLE_CONFIG,
  ELEVATIONS_ARCHIVED_OPTIONS,
  ELEVATIONS_ARCHIVED_TABLE_CONFIG,
  LotAppointmentDropdownOptions,
  CHATS_TABLE_CONFIG,
  CHATS_OPTIONS,
  BASE_HOME_PHOTOS_TABLE_CONFIG,
  BASE_HOME_PHOTOS_ACTIVE_OPTIONS,
  DEFAULT_PHASES_TABLE_ARCHIVED_CONFIG,
  PHASES_TABLE_ARCHIVED_CONFIG,
  BASE_HOME_DOCUMENTS_TABLE_CONFIG,
  BASE_HOME_DOCUMENTS_ACTIVE_OPTIONS,
  BASE_HOME_DOCUMENTS_ARCHIVED_OPTIONS,
  CONTACT_VENDORS_ARCHIVED_OPTIONS,
  CONTACT_VENDORS_ACTIVE_OPTIONS,
  CONTACT_VENDORS_TABLE_CONFIG,
  ARCHIVED_CHATS_OPTIONS,
  BASE_ACTIVE_HOMES_TABLE_CONFIG,
  BASE_ARCHIVED_HOMES_TABLE_CONFIG,
  TOOLTIPS_TEXTS
} from '@tripointe/tripointe-cms/constants';

import { formatText, capitalize, capitalizeAndRemoveUnderscores } from './text.utils';
import { toDateAsString } from './date.utils';
import { getAppointmentStartMoment } from './appointment-date.utils';
import { bytesToMb } from './file.utils';
import { getOptionsPerRowWithEditableCheck, INFO_OPTION } from './data-source-helpers';
import { MediaType } from 'libs/tripointe-cms/feature/home-photos/src/lib/services/home-gallery.service';

interface MapEntityPropertyValuesConfig {
  sorted?: boolean;
  unique?: boolean;
  propertyName?: string;
  nestedEntity?: string;
}

export enum LoginsStatus {
  Activated = 'Activated',
  Pending = 'Pending'
}

export const toDataSource = (item, config = {}) => {
  return Object.keys(item).reduce((acc, key) => {
    acc[key] = {
      value: item[key],
      ...config[key]
    };

    return acc;
  }, {});
};

const formatDate = (date: Date): string => {
  const timeZone = dayjs.tz.guess();

  return date ? dayjs(date).tz(timeZone).format('MM/DD/YYYY hh:mma z') : 'N/A';
};

// Users
export const userToDataSource = (user: User): TableEntry => {
  const {
    firstName,
    lastName,
    phone,
    role,
    divisions: divisionsArr,
    communities,
    initialPasswordSet,
    ...rest
  } = user;
  const name = `${firstName} ${lastName}`;
  const formatedPhone = formatText(phone, '###-###-####');

  const divisions = (divisionsArr || []).map(divisionObj => divisionObj.name);
  const divisionsOfAssignedCommunities = (communities || []).map(
    community => community.division.name
  );
  const uniqueDivisions = [...new Set([...divisions, ...divisionsOfAssignedCommunities])];
  const division = uniqueDivisions.join(', ');

  return toDataSource({
    ...rest,
    ...{
      name,
      phone: formatedPhone,
      division,
      role: role.label,
      initialPasswordSet
    }
  });
};

export const usersDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? BASE_USERS_ARCHIVED_OPTIONS : BASE_USERS_ACTIVE_OPTIONS;
  const optionsPerRow = getOptionsPerRowForUsers(dataSource, options, archived);

  return {
    ...BASE_USERS_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

const getOptionsPerRowForUsers = (
  dataSource: TableEntry[],
  options: TableOption[],
  archived: boolean
): TableOption[][] => {
  return (dataSource || []).map((record: TableEntry) => {
    const finalOptions = [...options];
    if (!record.initialPasswordSet.value && !archived) {
      finalOptions.splice(1, 0, { action: 'welcomeEmail', src: 'icons/mail' });
    }

    return finalOptions;
  });
};

// Divisions
export const divisionToDataSource = (division: Division): TableEntry => {
  const { id, phone, name, city, state, division149PhotosId, division149photosSync } = division;

  return toDataSource({
    id,
    name,
    city,
    state: state?.label,
    phone: formatText(phone, '###-###-####'),
    division149PhotosId,
    syncWith149PhotosInProgress: division149photosSync[0]?.syncInProgress
  });
};

export const divisionsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? DIVISIONS_ARCHIVED_OPTIONS : DIVISIONS_ACTIVE_OPTIONS;
  const optionsPerRow = getOptionsPerRowForDivisions(dataSource, options);

  return {
    ...DIVISIONS_TABLE_CONFIG,
    optionsPerRow,
    options,
    dataSource
  };
};

const getOptionsPerRowForDivisions = (
  dataSource: TableEntry[],
  options: TableOption[]
): TableOption[][] => {
  return (dataSource || []).map((record: TableEntry) => {
    const finalOptions = [...options];
    if (!record.division149PhotosId.value) {
      return finalOptions.filter(option => option.action !== TableAction.sync);
    } else if (!!record.syncWith149PhotosInProgress.value) {
      return finalOptions.map(option => {
        if (option.action === TableAction.sync) {
          return {
            ...option,
            disabled: true,
            tooltip: TOOLTIPS_TEXTS.divisionsTable.disabledSync
          };
        } else {
          return option;
        }
      });
    }

    return finalOptions;
  });
};

// Community
export const communityToDataSource = (community: Community): TableEntry => {
  const { id, division, name, city, state } = community;

  return toDataSource({
    id,
    name,
    city,
    division: division.name,
    state: state?.label
  });
};

export const communityDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? COMMUNITIES_ARCHIVED_OPTIONS : COMMUNITIES_ACTIVE_OPTIONS;

  return {
    ...COMMUNITIES_TABLE_CONFIG,
    options,
    dataSource
  };
};

export const communityContactsToDataSource = (
  contact: Contact,
  preselected: Object = {}
): TableEntry => {
  const {
    id,
    fullName: name,
    title: jobTitle,
    type,
    affiliation: companyType,
    mobilePhone,
    email,
    company
  } = contact;

  const checkbox = preselected[id] === true;

  return toDataSource({
    id,
    checkbox,
    name,
    jobTitle,
    companyType: capitalize(companyType),
    email,
    contactType: type?.label,
    company: company?.name,
    mobilePhone: formatText(mobilePhone, '###-###-####')
  });
};

export const communityContactsDataSourceWithConfig = (dataSource: TableEntry[]) => {
  const options = [];

  return {
    ...BASE_TEAM_MEMBERS_TABLE_CONFIG,
    options,
    dataSource
  };
};

// Contacts
export const contactToDataSource = (contact: Contact): TableEntry => {
  const {
    id,
    fullName: name,
    title: jobTitle,
    type,
    affiliation: companyType,
    mobilePhone,
    email,
    company,
    communities
  } = contact;

  const mergedDivision = [...new Set(communities.map(({ division }) => division.name))];
  const mergedCommunity = [...new Set(communities.map(community => community.name))];

  return toDataSource({
    id,
    name,
    jobTitle,
    companyType: capitalize(companyType),
    email,
    community: mergedCommunity,
    division: mergedDivision,
    contactType: type?.label,
    company: company?.name,
    mobilePhone: formatText(mobilePhone, '###-###-####')
  });
};

export const contactsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? CONTACTS_ARCHIVED_OPTIONS : CONTACTS_ACTIVE_OPTIONS;

  return {
    ...CONTACTS_TABLE_CONFIG,
    options,
    dataSource
  };
};

export const contactTypeToDataSource = (contactType: ContactType): TableEntry => {
  const { id, label: name, affiliation: companyType, description } = contactType;

  return toDataSource({
    id,
    name,
    companyType: capitalize(companyType),
    description
  });
};

const getOptionsPerRowForContactTypes = (
  dataSource: TableEntry[],
  options: TableOption[]
): TableOption[][] => {
  const READONLY_ROW = 'New Home Advisor';

  return (dataSource || []).map((record: TableEntry) => {
    switch (record.name.value) {
      case READONLY_ROW:
        return [INFO_OPTION];
      default:
        return options;
    }
  });
};

export const contactTypesDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? CONTACT_TYPES_ARCHIVED_OPTIONS : CONTACT_TYPES_ACTIVE_OPTIONS;
  const optionsPerRow = getOptionsPerRowForContactTypes(dataSource, options);

  return {
    ...CONTACT_TYPES_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

//vendors
export const contactVendorToDataSource = (vendor: Company): TableEntry => {
  const { id, name, state } = vendor;

  return toDataSource({
    id,
    name,
    state: state?.label
  });
};

export const contactVendorsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? CONTACT_VENDORS_ARCHIVED_OPTIONS : CONTACT_VENDORS_ACTIVE_OPTIONS;

  return {
    ...CONTACT_VENDORS_TABLE_CONFIG,
    options,
    dataSource
  };
};

//phases

export const phasesToDataSource = (
  phase: Partial<DefaultPhase | Phase>,
  isDefaultPhases = false,
  archived?: boolean
): TableEntry => {
  const { id, sequence, name, description, videoUrl } = phase;
  const allowedOptions = {
    id,
    name,
    description,
    isVideo: videoUrl ? 'Yes' : 'No'
  };
  if (!archived) allowedOptions['index'] = sequence;
  const prepareParamsToDataSource = {
    ...allowedOptions,
    ...(isDefaultPhases && {
      assignedDivisions: getMappedEntityPropertyValues((phase as DefaultPhase).divisionPhases, {
        nestedEntity: 'division'
      })
    })
  };

  return toDataSource(prepareParamsToDataSource);
};

export const phasesDataSourceWithConfig = (
  dataSource: TableEntry[],
  archived: boolean,
  isDefault: boolean
) => {
  const selectedConfig = isDefault
    ? archived
      ? DEFAULT_PHASES_TABLE_ARCHIVED_CONFIG
      : DEFAULT_PHASES_TABLE_CONFIG
    : archived
      ? PHASES_TABLE_ARCHIVED_CONFIG
      : PHASES_TABLE_CONFIG;
  const options = archived ? PHASES_ARCHIVED_OPTIONS : PHASES_ACTIVE_OPTIONS;

  return {
    ...selectedConfig,
    options,
    dataSource
  };
};

// phase tips
export const phaseTipToDataSource = (
  phaseTip: Partial<PhaseTip>,
  archived?: boolean
): TableEntry => {
  const { id, sequence, name, description } = phaseTip;
  const allowedOptions = {
    id,
    name,
    description
  };
  if (!archived) allowedOptions['index'] = sequence;

  return toDataSource(allowedOptions);
};

export const phaseTipsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? PHASE_TIPS_ARCHIVED_OPTIONS : PHASE_TIPS_ACTIVE_OPTIONS;
  const tableConfig = archived ? PHASE_TIPS_ARCHIVED_TABLE_CONFIG : PHASE_TIPS_TABLE_CONFIG;

  return {
    ...tableConfig,
    options,
    dataSource
  };
};

// schedule
export const eventToDataSource = (event: any): TableEntry => {
  const { name, description, communityPhaseAssignments: communityArr, editable, ...rest } = event;
  const communitiesArr = [];
  const divisionsArr = [];
  const phasesArr = [];
  (communityArr || []).forEach(comObj => {
    communitiesArr.push(comObj.community.name);
    divisionsArr.push(comObj.community.division.name);
    phasesArr.push(comObj.phase.name);
  });
  const communities = [...new Set([...communitiesArr])].join(', ');
  const divisions = [...new Set([...divisionsArr])].join(', ');
  const phases = [...new Set([...phasesArr])].join(', ');

  return toDataSource({
    ...rest,
    ...{ name, description, phases, divisions, communities, editable }
  });
};

export const eventsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? BASE_EVENTS_ARCHIVED_OPTIONS : BASE_EVENTS_ACTIVE_OPTIONS;
  const optionsPerRow = getOptionsPerRowWithEditableCheck(dataSource, options);

  return {
    ...BASE_EVENTS_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

// plans

export const plansToDataSource = (plan: Partial<Plan>): TableEntry => {
  const { id, name, communities, elevationsCount, editable } = plan;
  const { divisions, assignedCommunities } = getCommunitiesAndDivisionsForPlan(communities);

  return toDataSource({
    id,
    name,
    divisions,
    assignedCommunities,
    elevationsCount,
    editable
  });
};

export const plansDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? PLANS_ARCHIVED_OPTIONS : PLANS_ACTIVE_OPTIONS;
  const optionsPerRow = getOptionsPerRowWithEditableCheck(dataSource, options);

  return {
    ...PLANS_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

export const getCommunitiesAndDivisionsForPlan = (
  communities: Partial<Plan['communities']>
): { assignedCommunities: string; divisions: string } => {
  let divisions = '';
  const divisionsSet = [];
  const assignedCommunities = communities
    .map(community => {
      divisionsSet.push(community.division.name);

      return community.name;
    })
    .join(', ');
  divisions = [...new Set(divisionsSet)].join(', ');

  return {
    assignedCommunities,
    divisions
  };
};

//plans elevations

export const elevationstoDataSource = (
  elevation: Partial<Elevation>,
  archived?: boolean
): TableEntry => {
  const { id, name, sequence, imageUrl } = elevation;
  const allowedOptions = {
    checkbox: false,
    id,
    name,
    imageUrl
  };
  if (!archived) allowedOptions['index'] = sequence;

  return toDataSource(allowedOptions);
};

export const elevationsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? ELEVATIONS_ARCHIVED_OPTIONS : ELEVATIONS_ACTIVE_OPTIONS;
  const tableConfig = archived ? ELEVATIONS_ARCHIVED_TABLE_CONFIG : ELEVATIONS_TABLE_CONFIG;

  return {
    ...tableConfig,
    options,
    dataSource
  };
};

// documents
export const documentToDataSource = (document: Document): TableEntry => {
  const fileSizeInMb = Number(bytesToMb(document.fileSize).toFixed(2));

  const prepareParamsToDataSource = {
    id: document.id,
    name: document.name,
    description: document.description,
    fileSize: `${fileSizeInMb} MB`,
    phases: getMappedEntityPropertyValues(document.phases, { sorted: true, unique: true }),
    divisions: getMappedEntityPropertyValues(document.communities, {
      nestedEntity: 'division',
      unique: true
    }),
    communities: getMappedEntityPropertyValues(document.communities, {
      nestedEntity: 'community',
      unique: true
    }),
    fileName: document.fileName,
    fileUrl: document.fileUrl,
    editable: document.editable
  };

  return toDataSource(prepareParamsToDataSource);
};

export const documentsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? BASE_DOCUMENTS_ARCHIVED_OPTIONS : BASE_DOCUMENTS_ACTIVE_OPTIONS;
  const optionsPerRow = getOptionsPerRowWithEditableCheck(dataSource, options);

  return {
    ...BASE_DOCUMENTS_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

const getMappedEntityPropertyValues = <T>(
  entity: T[],
  config: MapEntityPropertyValuesConfig = {}
): string => {
  const { unique, sorted, nestedEntity, propertyName = 'name' } = config;
  /* Add checks of current[nestedEntity], current[nestedEntity][propertyName], current[propertyName] to prevent case when current[nestedEntity] exists but is null - MJD 2021-03-09 */
  const nameReducer = (acc: string[], current: Partial<Phase>) => [
    ...acc,
    nestedEntity && current[nestedEntity] && current[nestedEntity][propertyName]
      ? current[nestedEntity][propertyName]
      : !!current[propertyName]
        ? current[propertyName]
        : null
  ];
  let entityNames: string[] = entity.reduce(nameReducer, []);
  if (unique) {
    entityNames = [...new Set([...entityNames])];
    if (sorted) {
      entityNames = entityNames.sort((a, b) => {
        return a > b ? 1 : -1;
      });
    }
  }

  return entityNames.filter(Boolean).join(', ');
};

// homes
export const homeToDataSource = (home: Partial<Lot>): TableEntry => {
  const {
    id,
    address,
    community,
    buyerFirstName,
    buyerLastName,
    buyers,
    contractDate,
    elevation,
    homesite,
    phase,
    lastLoginDate,
    contractCancelledDate
  } = home;
  const getBuyerName = get('user.fullName');
  const buyersNames = (buyers || []).map(getBuyerName).join(', ');
  const prepareParamsToDataSource = {
    id,
    address,
    homesite,
    plan: elevation?.plan.name,
    buyers:
      String(!buyersNames.length ? `${buyerFirstName} ${buyerLastName}` : buyersNames).trim() ||
      'N/A',
    contractDate: !!contractDate ? toDateAsString(contractDate) : null,
    division: community?.division?.name,
    community: community?.name,
    phase: phase?.name,
    name: address,
    lastLoginDate: formatDate(lastLoginDate),
    contractCancelledDate: formatDate(contractCancelledDate)
  };

  return toDataSource(prepareParamsToDataSource);
};

export const homesDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? BASE_HOMES_ARCHIVED_OPTIONS : BASE_HOMES_ACTIVE_OPTIONS;
  const tableConfig = archived ? BASE_ARCHIVED_HOMES_TABLE_CONFIG : BASE_ACTIVE_HOMES_TABLE_CONFIG;

  return {
    ...tableConfig,
    options,
    dataSource
  };
};

// photos149
export const photos149ToDataSource = (data: Lot149PhotosGallery): any => {
  const { updatedAt, url, stage, type } = data;

  const prepareParamsToDataSource = {
    date: formatDate(updatedAt),
    stage,
    ...(type === MediaType.Photo
      ? { imageUrl: url }
      : {
        imageUrl: '',
        videoUrl: url
      })
  };

  return toDataSource(prepareParamsToDataSource);
};

// logins
export const loginsToDataSource = (data: LoginsToDataSourcePayload): TableEntry => {
  const { id, fullName: name, type, email, phone, status, user, lastLoginDate } = data.login;

  const formattedPhone = formatText(phone, '###-###-####');
  const buyerChatIdForHome = getBuyerConversationIdForHome(user.conversations, data.homeId);
  const prepareParamsToDataSource = {
    id,
    name,
    type,
    email,
    phone: formattedPhone,
    status,
    lastLoginDate: formatDate(lastLoginDate),
    hasConversation: !!buyerChatIdForHome,
    adminHasMessagesPermission: data.adminHasMessagesPermission,
    lotHasAssignedTeamMember: data.lotHasAssignedTeamMember,
    buyerUserId: user.id,
    source: 'logins',
    ...(buyerChatIdForHome && { chatId: buyerChatIdForHome })
  };

  return toDataSource(prepareParamsToDataSource);
};

const getBuyerConversationIdForHome = (conversations, homeId): number => {
  const conversationForHome = conversations.find(
    conversation =>
      conversation.status === ConversationStatusEnum.Active && conversation?.lot?.id === homeId
  );

  return conversationForHome?.id;
};

const getOptionsPerRowForLogins = (
  dataSource: TableEntry[],
  options: TableOption[]
): TableOption[][] => {
  return (dataSource || []).map((record: TableEntry) => {
    const displayMessagingIcon =
      record.adminHasMessagesPermission.value && record.lotHasAssignedTeamMember.value;

    switch (record.type.value) {
      case 'Buyer':
        return [
          { action: 'edit', src: 'icons/green-edit' },
          ...(record.status.value === LoginsStatus.Activated
            ? [{ action: 'welcomeEmail', src: 'icons/mail' }]
            : []),
          ...(displayMessagingIcon
            ? getConditionalMessagingIcon(record.hasConversation.value, record.status.value)
            : []),
          ...(record.status.value === LoginsStatus.Activated
            ? [{ action: 'copyLink', src: 'icons/copylink' }]
            : []),
          ...options
        ];
      default:
        return [
          { action: 'edit', src: 'icons/green-edit' },
          ...(record.status.value === LoginsStatus.Activated
            ? [{ action: 'welcomeEmail', src: 'icons/mail' }]
            : []),
          ...(displayMessagingIcon
            ? getConditionalMessagingIcon(record.hasConversation.value, record.status.value)
            : []),
          ...(record.status.value === LoginsStatus.Activated
            ? [{ action: 'copyLink', src: 'icons/copylink' }]
            : []),
          { action: 'delete', src: 'icons/delete' },
          ...options
        ];
    }
  });
};

const getConditionalMessagingIcon = (loginHasConversation, status): TableOption[] => {
  return status === LoginsStatus.Activated
    ? [
      loginHasConversation
        ? { action: 'replyToMessage', src: 'icons/reply' }
        : { action: 'sendMessage', src: 'icons/message' }
    ]
    : [];
};

export const photos149DataSourceWithConfig = (dataSource: TableEntry[]) => {
  return {
    ...BASE_HOME_PHOTOS_149_TABLE_CONFIG,
    options: [],
    dataSource
  };
};

export const loginsDataSourceWithConfig = (dataSource: TableEntry[]) => {
  const options = BASE_HOME_LOGINS_OPTIONS;
  const optionsPerRow = getOptionsPerRowForLogins(dataSource, options);

  return {
    ...BASE_HOME_LOGINS_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

export const homeContactsToDataSource = (
  contact: Contact,
  preselected: Object = {}
): TableEntry => {
  const {
    id,
    fullName: name,
    title: jobTitle,
    type,
    affiliation: companyType,
    mobilePhone,
    email,
    company
  } = contact;

  const checkbox = preselected[id] === true;

  return toDataSource({
    id,
    checkbox,
    name,
    jobTitle,
    companyType: capitalize(companyType),
    email,
    contactType: type?.label,
    company: company?.name,
    mobilePhone: formatText(mobilePhone, '###-###-####')
  });
};

export const homeContactsDataSourceWithConfig = (dataSource: TableEntry[]) => {
  const options = [];

  return {
    ...BASE_TEAM_MEMBERS_TABLE_CONFIG,
    options,
    dataSource
  };
};

export const homeScheduleToDataSource = (lotAppointment: LotAppointment): TableEntry => {
  const { id, description, phase, attendees, status } = lotAppointment;

  const appointmentDateTime = getAppointmentStartMoment(lotAppointment);

  let config;
  if (
    [LotAppointmentStatus.Pending, LotAppointmentStatus.Confirmed].some(
      s => s === lotAppointment.status
    )
  ) {
    const dropdownOptions = LotAppointmentDropdownOptions;
    const dropdownValue = lotAppointment.status;
    config = {
      status: {
        dropdownOptions,
        dropdownValue,
        type: 'dropdown'
      }
    };
  }

  return toDataSource(
    {
      id,
      date: appointmentDateTime.format('MM/DD/YYYY'),
      time: appointmentDateTime.format('hh:mm a'),
      name: lotAppointment.name,
      description,
      phase: phase.name,
      attendees: getMappedEntityPropertyValues(attendees, { propertyName: 'fullName' }),
      status: capitalizeAndRemoveUnderscores(status)
    },
    config
  );
};

const getOptionsPerRowForHomeSchedule = (
  dataSource: TableEntry[],
  options: TableOption[]
): TableOption[][] => {
  return (dataSource || []).map((record: TableEntry) => {
    switch (record.status.value) {
      case capitalizeAndRemoveUnderscores(LotAppointmentStatus.Pending):
        return [
          { action: 'edit', src: 'icons/green-edit' },
          { action: 'delete', src: 'icons/delete' },
          ...options
        ];
      case capitalizeAndRemoveUnderscores(LotAppointmentStatus.Confirmed):
        return [{ action: 'edit', src: 'icons/green-edit' }, ...options];
      default:
        return [{ action: 'view', src: 'icons/green-eye' }, ...options];
    }
  });
};

export const homeScheduleDataSourceWithConfig = (dataSource: TableEntry[]) => {
  const options = BASE_HOME_SCHEDULE_OPTIONS;
  const optionsPerRow = getOptionsPerRowForHomeSchedule(dataSource, options);

  return {
    ...BASE_HOME_SCHEDULE_TABLE_CONFIG,
    options,
    optionsPerRow,
    dataSource
  };
};

// messages(chats)
export const chatToDataSource = (chat: Conversation): TableEntry => {
  const { id, lastMessageCreatedAt, lot, buyerUser, teamMember } = chat;

  return toDataSource({
    id,
    name: `Conversation with ${buyerUser?.fullName}`,
    updatedAt: dayjs(lastMessageCreatedAt).format('MM/DD/YYYY hh:mm a'),
    divisionName: lot?.community.division.name,
    communityName: lot?.community.name,
    address: lot?.address,
    contactName: teamMember?.groupName,
    buyerName: buyerUser?.fullName
  });
};

export const chatsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived ? ARCHIVED_CHATS_OPTIONS : CHATS_OPTIONS;

  return {
    ...CHATS_TABLE_CONFIG,
    options,
    dataSource
  };
};

// home photos
export const homePhotosToDataSource = (photo: Partial<LotGalleryImage>): TableEntry => {
  const { id, order, fullImgSrc, smallImgSrc } = photo;
  const prepareParamsToDataSource = {
    checkbox: false,
    id,
    index: order,
    imageUrl: fullImgSrc,
    name: 'this photo',
    smallImg: smallImgSrc
  };

  return toDataSource(prepareParamsToDataSource);
};

export const homePhotosDataSourceWithConfig = (dataSource: TableEntry[]) => {
  const options = BASE_HOME_PHOTOS_ACTIVE_OPTIONS;

  return {
    ...BASE_HOME_PHOTOS_TABLE_CONFIG,
    options,
    dataSource
  };
};

const getHomeDocumentVisibleStatusByLotId = (document: Document, lotId: number) => {
  if (!document?.lotDocumentAssignments?.length) {
    return;
  }
  const assignment = document?.lotDocumentAssignments.find(lda => lda.lotId === lotId);

  return assignment ? assignment.visible : null;
};

// home documents
export const homeDocumentsToDataSource = (
  lotDocument: LotDocument | Document,
  lotId: number
): TableEntry => {
  const fileSizeInMb = Number(bytesToMb(lotDocument.fileSize).toFixed(2));
  const prepareParamsToDataSource = {
    id: lotDocument.id,
    name: lotDocument.name,
    description: lotDocument.description,
    fileSize: `${fileSizeInMb} MB`,
    phases: lotDocument.phases
      ? getMappedEntityPropertyValues(lotDocument.phases, { sorted: true, unique: true })
      : '',
    type: lotDocument.__typename,
    fileName: lotDocument.fileName,
    fileUrl: lotDocument.fileUrl,
    visible:
      lotDocument.__typename === 'Document'
        ? getHomeDocumentVisibleStatusByLotId(lotDocument as Document, lotId)
        : null
  };

  return toDataSource(prepareParamsToDataSource);
};

const getOptionsPerRowForHomeDocuments = (
  dataSource: TableEntry[],
  options: TableOption[]
): TableOption[][] => {
  return (dataSource || []).map((record: TableEntry) => {
    if (record.type.value === 'Document') {
      return [
        { action: 'download', src: 'icons/download-green' },
        {
          action: 'changeVisibility',
          src: record.visible.value ? 'icons/hide-grey' : 'icons/show-grey'
        },
        ...options
      ];
    } else {
      return [
        { action: 'edit', src: 'icons/green-edit' },
        { action: 'download', src: 'icons/download-green' },
        { action: 'delete', src: 'icons/delete' },
        ...options
      ];
    }
  });
};

export const homeDocumentsDataSourceWithConfig = (dataSource: TableEntry[], archived: boolean) => {
  const options = archived
    ? BASE_HOME_DOCUMENTS_ARCHIVED_OPTIONS
    : BASE_HOME_DOCUMENTS_ACTIVE_OPTIONS;

  const optionsPerRow = getOptionsPerRowForHomeDocuments(dataSource, options);

  return {
    ...BASE_HOME_DOCUMENTS_TABLE_CONFIG,
    options,
    ...(!archived && { optionsPerRow }),
    dataSource
  };
};
